<template>
    <div
      ref="triggerRef"
      class="pcb-helIcon-text_tooltip-container"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      <div class="pcb-helIcon-text_help-icon">
        <svg
          t="1754458623327"
          class="pcb-helIcon-text_icon"
          viewBox="0 0 1024 1024"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          p-id="11907"
          width="16"
          height="16"
        >
          <path
            d="M502.5 952c-33.946 0-61.5-27.554-61.5-61.5s27.554-61.5 61.5-61.5 61.5 27.554 61.5 61.5-27.554 61.5-61.5 61.5zM766.671 353.069c-3.55 65.792-23.709 117.658-124.507 218.549-51.11 51.155-83.337 89.522-86.602 123.2-2.555 26.43-24.845 46.182-50.825 46.182-1.704 0-3.407-0.142-5.11-0.284-28.11-2.7-48.555-27.852-45.857-55.987 6.956-70.908 58.917-128.174 116.131-185.44 91.57-91.654 93.132-120.784 94.694-151.62 1.845-35.951-11.358-70.765-37.48-98.19-29.672-31.262-71.695-49.167-115.28-49.167h-0.283c-83.905 0.142-152.334 68.634-152.334 152.757 0 28.278-22.857 51.156-51.109 51.156-28.252 0-51.109-22.878-51.109-51.156 0-68.066 26.406-132.01 74.392-180.182S443.264 98.143 511.268 98c71.694-0.142 140.975 29.415 189.955 80.997 45.714 47.888 68.855 109.7 65.448 174.072z"
            fill="#c1c1c1"
            p-id="11908"
          ></path>
        </svg>
      </div>
    </div>
    
    <!-- 使用 Teleport 将 tooltip 挂载到 body -->
    <Teleport to="body">
      <div 
        v-if="visible" 
        ref="tooltipRef"
        class="pcb-helIcon-text_tooltip" 
        :style="tooltipStyle"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
      >
        <div v-html="content"></div>
      </div>
    </Teleport>
  </template>
  
  <script setup>
  import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
  
  defineOptions({
    name: 'help',
  });
  
  const props = defineProps({
    content: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: 'bottom', // 默认位置为 bottom（向下显示）
    },
    width:{
      type:Number,
      default:600
    }
  });
  
  const visible = ref(false);
  const triggerRef = ref(null); // 触发元素的 ref
  const tooltipRef = ref(null); // tooltip 元素的 ref
  const tooltipPosition = ref({ top: 0, left: 0 });
  
  // 计算 tooltip 位置
  const calculatePosition = () => {
    if (!triggerRef.value) return;
    
    const triggerRect = triggerRef.value.getBoundingClientRect();
    const tooltipRect = tooltipRef.value?.getBoundingClientRect() || { width: props.width, height: 100 };
    
    let top = 0;
    let left = 0;
    
    switch (props.position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - 4;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + 4;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - 4;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + 4;
        break;
      default:
        // 默认向下显示（bottom）
        top = triggerRect.bottom + 4;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
    }
    
    // 防止超出视口
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // 右边界检测
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }
    // 左边界检测
    if (left < 10) {
      left = 10;
    }
    // 上边界检测
    if (top < 10) {
      top = triggerRect.bottom + 4; // 强制显示在下方
    }
    // 下边界检测
    if (top + tooltipRect.height > viewportHeight - 10) {
      top = triggerRect.top - tooltipRect.height - 4; // 强制显示在上方
    }
    
    tooltipPosition.value = { top, left };
  };
  
  // 显示提示框
  const showTooltip = () => {
    visible.value = true;
    nextTick(() => {
      calculatePosition();
    });
  };
  
  // 隐藏提示框
  const hideTooltip = () => {
    visible.value = false;
  };
  
  // tooltip 样式
  const tooltipStyle = computed(() => ({
    position: 'fixed',
    top: `${tooltipPosition.value.top}px`,
    left: `${tooltipPosition.value.left}px`,
    maxWidth: `${props.width}px`,
    zIndex: 9999,
  }));
  
  // 监听滚动和窗口大小变化，重新计算位置
  const handleScroll = () => {
    if (visible.value) {
      calculatePosition();
    }
  };
  
  onMounted(() => {
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
  });
  
  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleScroll);
  });
  </script>
  
  <style scoped>
  .pcb-helIcon-text_tooltip-container {
    display: inline-block;
  }
  
  .pcb-helIcon-text_help-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--help-icon-size, 16px);
    height: var(--help-icon-size, 16px);
    border-radius: 50%;
    background-color: var(--help-icon-bg, #f0f0f0);
    color: var(--help-icon-color, #666);
    cursor: help;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
    will-change: background-color, color, transform;
    transform: scale(1);
  }
  
  .pcb-helIcon-text_help-icon:hover {
    background-color: var(--primary-color, #0060FF);
    color: #fff;
  }
  
  .pcb-helIcon-text_help-icon svg {
    width: calc(var(--help-icon-size, 16px) * 0.7);
    height: calc(var(--help-icon-size, 16px) * 0.7);
  }
  </style>
  
  <style>
  /* 全局样式，不使用 scoped，因为 tooltip 在 body 下 */
  .pcb-helIcon-text_tooltip {
    position: fixed;
    min-width: 200px;
    max-width: 600px;
    background-color: #fff;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.5em;
    color: #333;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    z-index: 9999;
    animation: tooltipFadeIn 0.2s ease-in-out;
    pointer-events: auto;
  }
  
  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  </style>
  